import { Box, IconButton, Tooltip } from '@mui/material';
import BkTable from 'src/components/table/BkTable';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AppDispatch, RootState } from 'src/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { modifyAutoExclusions } from 'src/features/company/utils';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddExclusion from './modals/AddExclusion';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Typography } from 'src/components/new/Typography';
import { handleAutoExclusionChange } from 'src/features/company/companySlice';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { setCurrentUser } from 'src/features/user/userSlice';

const columns = [
  { headerName: 'Name', field: 'name', headerCustomStyles: { width: '85%' } },
  { headerName: 'Action', field: 'action' },
];

const AutoExcludeList: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const exclusions = useSelector((state: RootState) => state.user.currentUser.thirteenTrusteeAddressExclusions) || [];
  const exclusionName = useSelector((state: RootState) => state.company.autoExclusionForm.value);

  const [showAutoExcludeModal, setShowAutoExcludeModal] = useState<boolean>(false);
  const [nameToEdit, setNameToEdit] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const isEditing = nameToEdit !== '' && exclusions.includes(nameToEdit);

  const isOrgAdmin = currentUser.role === 'admin';

  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  useEffect(() => {
    const data = [];
    if (exclusions.length) {
      for (let name of exclusions) {
        data.push({
          name,
          action: renderAction(name),
        });
      }
      setData(data);
    }
  }, [exclusions]);

  const onCreateClick = () => {
    setShowAutoExcludeModal(true);
  };

  const onEditClick = (name: string) => {
    dispatch(handleAutoExclusionChange(name));
    setShowAutoExcludeModal(true);
    setNameToEdit(name);
  };

  const onDeleteName = (name: string) => {
    const modifiedExclusions = exclusions.filter((item) => item !== name);

    dispatch(modifyAutoExclusions(modifiedExclusions)).then((result) => {
      const success = modifyAutoExclusions.fulfilled.match(result);

      setSnackbarState({
        ...snackBarState,
        isOpened: true,
        message: success ? `Successfully removed ${name} from the list!` : 'Something went wrong',
        severity: success ? 'success' : 'error',
      });

      if (success) {
        dispatch(setCurrentUser({ user: { ...currentUser, thirteenTrusteeAddressExclusions: modifiedExclusions } }));
      }
    });
  };

  const onAddName = (name: string) => {
    const modifiedExclusions = isEditing
      ? exclusions.map((item) => (item === nameToEdit ? name : item))
      : [...exclusions, name];

    dispatch(modifyAutoExclusions(modifiedExclusions)).then((result) => {
      const success = modifyAutoExclusions.fulfilled.match(result);

      setSnackbarState({
        ...snackBarState,
        isOpened: true,
        message: success
          ? isEditing
            ? `Successfully renamed ${nameToEdit} to ${name}!`
            : 'Successfully added name to the list!'
          : 'Something went wrong',
        severity: success ? 'success' : 'error',
      });

      if (success) {
        dispatch(setCurrentUser({ user: { ...currentUser, thirteenTrusteeAddressExclusions: modifiedExclusions } }));
      }
    });
  };

  const onModalClose = () => {
    setShowAutoExcludeModal(false);
    dispatch(handleAutoExclusionChange(''));
    setNameToEdit('');
  };

  const renderAction = (name: string) => {
    return (
      <Tooltip
        disableHoverListener={isOrgAdmin}
        disableFocusListener={isOrgAdmin}
        disableTouchListener={isOrgAdmin}
        title="Only admins can edit or remove exclusions"
      >
        <Box sx={{ justifyContent: 'center', display: 'flex', gap: 2 }}>
          <IconButton disabled={!isOrgAdmin} onClick={() => onEditClick(name)}>
            <CreateOutlinedIcon />
          </IconButton>
          <IconButton disabled={!isOrgAdmin} onClick={() => onDeleteName(name)}>
            <DeleteOutlineOutlinedIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ padding: '2.5rem 1.5rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem' }} fontWeight={400}>
            Auto Exclusions
          </Typography>
          <BkToolTip
            title={
              <p>
                Names will be auto-excluded from <strong>all</strong> notices. If the match is not{' '}
                <strong>exact</strong>, no exclusions will occur.
              </p>
            }
            Icon={InfoOutlinedIcon}
          />
        </Box>
        <Button sx={{ width: '18.75rem' }} type="outline" onClick={onCreateClick}>
          <AddOutlinedIcon />
          <Spacer w={8} />
          Add Exclusions
        </Button>
      </Box>
      <BkTable columns={columns} data={data || []} initialRowsPerPage={20} renderPagination={false} />
      <AddExclusion
        mode={isEditing ? 'Edit' : 'Add'}
        open={showAutoExcludeModal}
        onClose={onModalClose}
        onSuccess={() => onAddName(exclusionName)}
      />
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </Box>
  );
};

export default AutoExcludeList;
